import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Session configuration
const SESSION_CONFIG = {
  checkInterval: 5 * 60 * 1000, // Check every 5 minutes
  warningThreshold: 5 * 60 * 1000, // Warn 5 minutes before expiry
  inactivityTimeout: 30 * 60 * 1000, // 30 minutes of inactivity
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFirstLogin, setShowFirstLogin] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const sessionCheckRef = useRef(null);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSessionWarning(false);
  }, []);

  // Check session validity
  const checkSession = useCallback(async () => {
    if (!user) return;
    
    const inactiveTime = Date.now() - lastActivityRef.current;
    
    // Check for inactivity timeout
    if (inactiveTime >= SESSION_CONFIG.inactivityTimeout) {
      console.log('[Session] Timeout due to inactivity');
      await logout();
      return;
    }
    
    // Show warning if approaching timeout
    if (inactiveTime >= SESSION_CONFIG.inactivityTimeout - SESSION_CONFIG.warningThreshold) {
      setSessionWarning(true);
    }
    
    // Validate token with server
    try {
      await api.get('/auth/me');
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('[Session] Token expired');
        setUser(null);
        setSessionWarning(false);
      }
    }
  }, [user]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  // Set up session check interval
  useEffect(() => {
    if (user) {
      sessionCheckRef.current = setInterval(checkSession, SESSION_CONFIG.checkInterval);
    }
    
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [user, checkSession]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      updateActivity();
      // Check if user needs first-login experience
      if (response.data && !response.data.first_login_acknowledged) {
        // Roles that need first-login experience
        const needsFirstLogin = ['bank_partner', 'client_admin', 'auditor', 'regulator', 
          'granullo_ops', 'granullo_tech', 'granullo_security'].includes(response.data.role);
        if (needsFirstLogin) {
          setShowFirstLogin(true);
        }
      }
    } catch (error) {
      // ONLY clear the user on a genuine auth failure (401). Transient errors
      // (429 rate limit, 502/503 network glitches) must NOT log the user out
      // — that was the root cause of mid-session redirects to /login.
      const status = error.response?.status;
      if (status === 401) {
        setUser(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('granullo_token');
        }
      }
      // For any other status (including 429), keep the prior user state.
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    
    // If 2FA is required, don't set user yet - let Login page handle it
    if (response.data.requires_2fa || response.data.requires_2fa_setup) {
      return response.data;
    }
    
    // Persist Bearer token for subsequent requests (Cloudflare strips cookies)
    if (response.data.token) {
      localStorage.setItem('granullo_token', response.data.token);
      // Backwards-compat shim — older surfaces still read 'token'
      localStorage.setItem('token', response.data.token);
    }
    setUser(response.data.user);
    updateActivity();
    // Check if first-login needed after login
    if (response.data.user && !response.data.user.first_login_acknowledged) {
      const needsFirstLogin = ['bank_partner', 'client_admin', 'auditor', 'regulator', 
        'granullo_ops', 'granullo_tech', 'granullo_security'].includes(response.data.user.role);
      if (needsFirstLogin) {
        setShowFirstLogin(true);
      }
    }
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore logout errors
    }
    setUser(null);
    setShowFirstLogin(false);
    setSessionWarning(false);
    localStorage.removeItem('granullo_token');
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  // Extend session (dismiss warning and reset timer)
  const extendSession = useCallback(() => {
    updateActivity();
    setSessionWarning(false);
  }, [updateActivity]);

  const acknowledgeFirstLogin = async () => {
    try {
      await api.post('/auth/acknowledge-first-login', { acknowledged: true });
      setUser(prev => ({ ...prev, first_login_acknowledged: true }));
      setShowFirstLogin(false);
    } catch (error) {
      console.error('Error acknowledging first login:', error);
      setShowFirstLogin(false);
    }
  };

  // RBAC helpers
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'founder_superadmin' || user?.role === 'client_admin';
  const isAnalyst = user?.role === 'analyst' || isAdmin;
  const isViewer = user?.role === 'viewer' || isAnalyst;
  const canWrite = isAnalyst; // admin and analyst can write
  const canManageUsers = isAdmin;

  const value = {
    user,
    setUser,
    loading,
    login,
    logout,
    checkAuth,
    showFirstLogin,
    acknowledgeFirstLogin,
    sessionWarning,
    extendSession,
    isAdmin,
    isAnalyst,
    isViewer,
    canWrite,
    canManageUsers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};